import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Actions, ofActionDispatched, Select, Store } from '@ngxs/store';
import { ExtendedAttributeFilterInterface } from 'app/library/all-users/interfaces/extended-attribute-filer.interface';
import { OfferingType } from 'app/library/course-offering/enums/offering-type.enum';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { Observable, race, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import {
    CreateCourseStructureCategory,
    CreateCourseStructureCategorySuccess,
    CreateCourseStructureSubCategory,
    CreateCourseStructureSubCategoryFailure,
    CreateCourseStructureSubCategorySuccess,
    DeleteCourseStructureCategory,
    DeleteCourseStructureCategoryFailure,
    DeleteCourseStructureCategorySuccess,
    FetchCourseStructureCategories,
    FetchCourseStructureCategoriesFailure,
    FetchCourseStructureCategoriesSuccess,
    FetchCourseStructureCategoriesTree,
    FetchCourseStructureCategoriesTreeFailure,
    FetchCourseStructureCategoriesTreeSuccess,
    FetchCourseStructureCategory,
    FetchCourseStructureCategoryFailure,
    FetchCourseStructureCategorySubcategories,
    FetchCourseStructureCategorySubcategoriesFailure,
    FetchCourseStructureCategorySubcategoriesSuccess,
    FetchCourseStructureCategorySuccess,
    MoveCourseStructureCategory,
    MoveCourseStructureCategorySuccess,
    SearchCourseStructureCategories,
    SearchCourseStructureCategoriesFailure,
    SearchCourseStructureCategoriesSuccess,
    UpdateCourseStructureCategory,
    UpdateCourseStructureCategoryFailure,
    UpdateCourseStructureCategoryOrder,
    UpdateCourseStructureCategoryOrderFailure,
    UpdateCourseStructureCategoryOrderSuccess,
    UpdateCourseStructureCategorySuccess,
} from '../actions/index';
import { CourseStructureCategoriesState } from '../models/course-structure-categories.state';
import { CourseStructureCategoryTree } from '../models/course-structure-category-tree.model';
import { CourseStructureCategory } from '../models/course-structure-category.model';
import { CourseStructureCategoryState } from '../models/course-structure-category.state';
import { CourseStructureSubcategory } from '../models/course-structure-subcategory.model';
import { ICourseStructureSubCategoryActionPayload } from '../interfaces/course-structure-subcategory.action-payload.interface';

@Injectable()
export class CourseStructureCategoryService {
    @Select(CourseStructureCategoryState.getEntity)
    courseStructureCategory$: Observable<CourseStructureCategory>;

    @Select(CourseStructureCategoryState.getEntities)
    courseStructureCategories$: Observable<CourseStructureCategory[]>;

    @Select(CourseStructureCategoriesState.getFilteredEntities)
    filteredCourseStructureCategories$: Observable<CourseStructureCategory[]>;

    private _newCourseStructureCategorySubject = new Subject<CourseStructureCategory>();
    public newCourseStructureCategorySubject$ = this._newCourseStructureCategorySubject.asObservable();

    constructor(private _store: Store, private _action$: Actions, private _actionHelper: NgxsActionHelper, private _translateService: TranslateService) {}

    getCourseStructureCategories(): CourseStructureCategory[] {
        return this._store.selectSnapshot(CourseStructureCategoriesState.getFilteredEntities);
    }

    getCourseStructureCategoryById(id: string): CourseStructureCategory {
        return CourseStructureCategoryState.getEntity(this._store.selectSnapshot(CourseStructureCategoryState), id);
    }

    async fetchCourseStructureCategory(courseStructureCategoryId: string): Promise<boolean> {
        this._store.dispatch(new FetchCourseStructureCategory(courseStructureCategoryId));

        return await this._actionHelper.race(FetchCourseStructureCategorySuccess, FetchCourseStructureCategoryFailure).toPromise();
    }

    async fetchCourseStructureCategories(): Promise<boolean> {
        this._store.dispatch(new FetchCourseStructureCategories());

        return await this._actionHelper.race(FetchCourseStructureCategoriesSuccess, FetchCourseStructureCategoriesFailure).toPromise();
    }

    async createCourseStructureCategory(courseOfferingsCategory: any, shouldNavigateToNewCategory: boolean = true) {
        this._store.dispatch(new CreateCourseStructureCategory(courseOfferingsCategory));

        if (shouldNavigateToNewCategory) {
            this._action$.pipe(ofActionDispatched(CreateCourseStructureCategorySuccess), take(1)).subscribe((action: CreateCourseStructureCategorySuccess) => {
                this._newCourseStructureCategorySubject.next(action.payload);
            });
        }
    }

    async createCourseStructureCategorySubcategory(courseStructureSubcategory: ICourseStructureSubCategoryActionPayload): Promise<CourseStructureSubcategory> {
        this._store.dispatch(new CreateCourseStructureSubCategory(courseStructureSubcategory));

        return race(
            this._action$.pipe(
                ofActionDispatched(CreateCourseStructureSubCategorySuccess),
                map((action: CreateCourseStructureSubCategorySuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(CreateCourseStructureSubCategoryFailure),
                map((action: CreateCourseStructureSubCategoryFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async updateCourseStructureCategory(courseStructureCategory: CourseStructureCategory) {
        this._store.dispatch(new UpdateCourseStructureCategory(courseStructureCategory));

        return await this._actionHelper.race(UpdateCourseStructureCategorySuccess, UpdateCourseStructureCategoryFailure).toPromise();
    }

    async updateCourseStructureCategoryOrder(courseStructureCategory: CourseStructureCategory) {
        this._store.dispatch(new UpdateCourseStructureCategoryOrder(courseStructureCategory));

        return await this._actionHelper.race(UpdateCourseStructureCategoryOrderSuccess, UpdateCourseStructureCategoryOrderFailure).toPromise();
    }

    deleteCourseStructureCategory(courseStructureCategoryId: string) {
        this._store.dispatch(new DeleteCourseStructureCategory(courseStructureCategoryId));

        return race(
            this._action$.pipe(
                ofActionDispatched(DeleteCourseStructureCategorySuccess),
                map((action: DeleteCourseStructureCategorySuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(DeleteCourseStructureCategoryFailure),
                map(() => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchCourseStructureCategoriesTree(courseOfferingType: number): Promise<CourseStructureCategoryTree[]> {
        this._store.dispatch(new FetchCourseStructureCategoriesTree({ courseOfferingType }));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchCourseStructureCategoriesTreeSuccess),
                map((action: FetchCourseStructureCategoriesTreeSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchCourseStructureCategoriesTreeFailure),
                map((action: FetchCourseStructureCategoriesTreeFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async fetchCourseStructureCategorySubcategories(categoryId: string): Promise<CourseStructureSubcategory[]> {
        this._store.dispatch(new FetchCourseStructureCategorySubcategories(categoryId));

        return race(
            this._action$.pipe(
                ofActionDispatched(FetchCourseStructureCategorySubcategoriesSuccess),
                map((action: FetchCourseStructureCategorySubcategoriesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(FetchCourseStructureCategorySubcategoriesFailure),
                map((action: FetchCourseStructureCategorySubcategoriesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    async searchCourseStructureCategories(
        searchTerm: string,
        fetchCourseStructureCategoriesDataAsTree: boolean,
        courseOfferingType?: OfferingType,
        tags?: string[],
        types?: string[],
        extendedAttributesFilters?: ExtendedAttributeFilterInterface[],
        columns?: string[]
    ): Promise<CourseStructureCategoryTree[]> {
        this._store.dispatch(
            new SearchCourseStructureCategories({ searchTerm, fetchCourseStructureCategoriesDataAsTree, courseOfferingType, tags, types, extendedAttributesFilters, columns })
        );

        return race(
            this._action$.pipe(
                ofActionDispatched(SearchCourseStructureCategoriesSuccess),
                map((action: SearchCourseStructureCategoriesSuccess) => action.payload)
            ),
            this._action$.pipe(
                ofActionDispatched(SearchCourseStructureCategoriesFailure),
                map((action: SearchCourseStructureCategoriesFailure) => null)
            )
        )
            .pipe(take(1))
            .toPromise();
    }

    moveCourseStructureCategory(categoryId: string, parentId: string) {
        this._store.dispatch(new MoveCourseStructureCategory({ categoryId, parentId }));

        this._action$.pipe(ofActionDispatched(MoveCourseStructureCategorySuccess), take(1)).subscribe((action: MoveCourseStructureCategorySuccess) => {
            this._newCourseStructureCategorySubject.next(action.payload);
        });
    }

    searchTree(courseStructureCategoriesTree, matchingId: string) {
        if (courseStructureCategoriesTree.id == matchingId) {
            return courseStructureCategoriesTree;
        } else if (courseStructureCategoriesTree.children != null) {
            var i;
            var result = null;
            for (i = 0; result == null && i < courseStructureCategoriesTree.children.length; i++) {
                result = this.searchTree(courseStructureCategoriesTree.children[i], matchingId);
            }
            return result;
        }
        return null;
    }

    searchTreeName(courseStructureCategoriesTree, matchingId: string, names: string = ''): string {
        if (courseStructureCategoriesTree.id == matchingId) {
            names = names + '/' + courseStructureCategoriesTree.name;
            names = names.substring(1); // removes first '/' charachter
            return names.substring(names.indexOf('/') + 1); // removes default audience from a path
        } else if (courseStructureCategoriesTree.children != null) {
            var i;
            var result = null;
            names = names + '/' + courseStructureCategoriesTree.name;
            for (i = 0; result == null && i < courseStructureCategoriesTree.children.length; i++) {
                result = this.searchTreeName(courseStructureCategoriesTree.children[i], matchingId, names);
            }
            return result;
        }
        return null;
    }

    compareAndAssignProperties(originalTree: CourseStructureCategory[], newTree: CourseStructureCategory[]): CourseStructureCategory[] {
        const mergedTree: CourseStructureCategory[] = [];

        for (let i = 0; i < originalTree.length; i++) {
            const originalNode = originalTree[i];
            const newNode = newTree.find((item) => item.id === originalNode.id);

            if (newNode) {
                // Clone the original node to avoid modifying the original data
                const mergedNode: CourseStructureCategory = { ...originalNode };

                // Assign properties from the new tree
                mergedNode.open = newNode.open || false;
                mergedNode.isSelected = newNode.isSelected || false;

                // Recursively process children
                if (originalNode.children && originalNode.children.length > 0) {
                    mergedNode.children = this.compareAndAssignProperties(originalNode.children, newNode.children || []);
                }

                // Add the merged node to the result list
                mergedTree.push(mergedNode);
            } else {
                mergedTree.push(originalTree[i]);
            }
        }

        return mergedTree;
    }

    convertListToTree(list: CourseStructureCategory[], isRootCategoryNeeded: boolean = false): CourseStructureCategory[] {
        // Sort the flat list based on the 'sort' property
        list.sort((a, b) => (a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0));

        const map: { [id: number]: number } = {}; // Using an index signature for the map
        let roots: CourseStructureCategory[] = [];
        let toReturn: CourseStructureCategory[];

        let node: CourseStructureCategory, i: number;

        for (i = 0; i < list.length; i += 1) {
            map[list[i].id] = i; // initialize the map
            list[i].children = []; // initialize the children
        }

        for (i = 0; i < list.length; i += 1) {
            node = list[i];
            if (node?.parent?.id !== undefined) {
                if (list[map[node.parent.id]]) {
                    list[map[node.parent.id]].children.push(node);
                }
            } else {
                // Otherwise, it's a root node
                roots.push(node);
            }
        }

        // Sort roots based on the 'sort' property
        roots.sort((a, b) => (a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0));

        // Sort children of each root based on the 'sort' property
        roots.forEach((root) => {
            root.children.sort((a, b) => (a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0));
        });

        if (isRootCategoryNeeded) {
            const rootCategory = new CourseStructureCategory({
                id: null,
                name: this._translateService.instant('COURSE_OFFERINGS.LABEL.CATEGORY'),
                type: 'uninitialized',
                sort: 0,
                parent: null,
            });

            rootCategory.children = roots;

            toReturn = [rootCategory];
        } else {
            toReturn = roots;
        }

        return toReturn;
    }
}
